import React, { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import styles from "./MenuItem.module.css"
import { useDispatch, useSelector } from "react-redux"
import classNames from "classnames"
import { useNavigate } from "react-router-dom"
import { getProWizardErrorsObject, useIsCompleted } from "./useCompletion"
import { CircledIcon } from "ui/CircledIcon"
import { receiveErrors } from "modules/errors/reducer"
import { isStripeChargesEnabledSelector, isPaypalConnectedSelector } from "store/user"
import { useSaveContext } from "./SaveContext"

export const MenuItem = ({ item }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { activeMenu, validation } = useSelector((state) => state.proWizardMenu)
  const user = useSelector((store) => store.user)
  const isStripeChargeEnabled = useSelector(isStripeChargesEnabledSelector)
  const isPaypalConnected = useSelector(isPaypalConnectedSelector)
  const { triggerSave } = useSaveContext()
  const { name, id, progressWeight, path } = item

  const isActive = activeMenu === id
  const isCompleted = useIsCompleted(id)

  const onClickHandler = useCallback(() => {
    if (path) {
      // Trigger save before navigating
      triggerSave(() => {
        navigate(path)
        if (validation) {
          const errorsObject = getProWizardErrorsObject(user)
          dispatch(
            receiveErrors({
              data: { message: errorsObject },
              showToast: false
            })
          )
        }
      })
    }
  }, [path, navigate, validation, user, dispatch, triggerSave])

  const renderIcon = useMemo(() => {
    if (progressWeight <= 0) return null

    const alertIcon = <CircledIcon name="AlertCircle" colorType="error" />
    const checkIcon = <CircledIcon name="Check" colorType="success" />

    // For payment provider (optional section)
    if (id === "connect-payment-provider") {
      // Show green check if either Stripe charges are enabled OR PayPal is connected
      const hasPaymentProvider = isStripeChargeEnabled || isPaypalConnected
      if (hasPaymentProvider) {
        return checkIcon
      }
      // Don't show error icon for optional section (it's not required)
      return null
    }

    // For licenses (optional section)
    if (id === "licenses-and-certifications") {
      // Only show check if completed, no error icon (it's not required)
      return isCompleted ? checkIcon : null
    }

    // For required sections
    if (isCompleted) {
      return checkIcon
    }

    // Only show error icon if validation is enabled
    return validation ? alertIcon : null
  }, [progressWeight, id, isCompleted, isStripeChargeEnabled, isPaypalConnected, validation])

  return (
    <div
      className={classNames(styles.menuGroupItem, isActive && styles.menuGroupItemActive, isCompleted && styles.menuGroupItemCompleted)}
      onClick={onClickHandler}
    >
      {name}
      {renderIcon}
    </div>
  )
}

MenuItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    progressWeight: PropTypes.number.isRequired,
    path: PropTypes.string
  }).isRequired
}
